import { ReactNode } from 'react';
import clsx from 'clsx';

// components
import Typography, { TypographyProps } from '@/components/elements/Typography';

// types
type Size = `small` | `medium` | `large`;

export type TabButtonProps = {
  size?: Size;
  children: ReactNode;
  selected?: boolean;

  // TabButton icons
  startIcon?: ReactNode;
  endIcon?: ReactNode;
} & Omit<TypographyProps<'button'>, 'variant'>;

// TabButton variants
const SIZE_MAPS: Record<Size, string> = {
  small: `py-0.5 px-2.5 text-xs md:text-sm text-sm`,
  medium: `py-1 px-3 text-sm md:text-md min-w-[85px]`,
  large: `py-2 px-3 text-md`,
};

/**
 * @component TabButton
 */
function TabButton({
  size = `medium`,
  startIcon,
  endIcon,
  children,
  className,
  selected,
  ...rest
}: TabButtonProps) {
  const classes = clsx(
    `transition select-none font-normal hover:bg-gray-100 rounded-2xl rounded-[3px] focus:ring-2 text-gray-800 focus:ring-gray-300 font-light focus:outline-none flex flex-row items-center justify-center`,
    {
      'bg-gray-100 hover:cursor-default text-gray-800': selected,
      'active:bg-gray-400 text-gray-600': !selected,
    },
    SIZE_MAPS[size],
    className,
  );
  return (
    <Typography variant="no-style" as="button" className={classes} {...rest}>
      {startIcon && <div className="mr-2">{startIcon}</div>}

      {children}

      {endIcon && <div className="ml-2">{endIcon}</div>}
    </Typography>
  );
}

export default TabButton;
